import { useEffect, useRef, useCallback } from 'react';
import {
  startSession,
  endSession,
  updateSession,
  getStoredSession,
  isLatestUpdateWithinGracePeriod,
} from '../utils/SessionUtils';
import {
  initializeActivityTracking,
  cleanupActivityTracking,
  getLatestActivityTime,
  isLatestActivityWithinGracePeriod,
} from '../utils/ActivityUtils';
import { UPDATE_INTERVAL_MS } from '../utils/UserAnalyticsContants';

/**
 * Internal state for tracking session and activity status
 */
interface TrackingState {
  /** Whether activity tracking is currently active */
  initialized: boolean;
  /** Timer for periodic session updates */
  updateInterval: NodeJS.Timeout | null;
  /** Timestamp of last successful session update */
  lastUpdateTime: number;
}

interface IUseUserAnalyticsConfig {
  enabled: boolean;
  getToken: () => Promise<string | undefined>;
}

type IUseUserAnalyticsProps = Partial<IUseUserAnalyticsConfig>;

const defaultConfig: IUseUserAnalyticsConfig = {
  enabled: false,
  getToken: () => Promise.resolve(undefined),
};

/**
 * Hook to manage user session lifecycle based on page visibility and user activity.
 *
 * Handles:
 * - Starting new sessions or resuming existing ones
 * - Tracking user activity
 * - Periodic session updates
 * - Cleanup when page is hidden or user loses access
 * @param config - Optional configuration that overrides default settings
 */
export function useUserAnalytics(config: IUseUserAnalyticsProps = {}): void {
  const { enabled: isUserAnalyticsEnabled, getToken }: IUseUserAnalyticsConfig =
    {
      ...defaultConfig,
      ...config,
    };

  // Track session state and configuration across renders
  const trackingState = useRef<TrackingState>({
    initialized: false,
    updateInterval: null,
    lastUpdateTime: 0,
  });

  // Guard flags to prevent concurrent operations
  const isInitializing = useRef(false);
  const isCleaningUp = useRef(false);

  /**
   * Stops tracking and cleans up session resources.
   * Ensures cleanup only happens once even if called multiple times.
   */
  const stopTracking = useCallback(async (): Promise<void> => {
    if (!trackingState.current.initialized || isCleaningUp.current) {
      return;
    }

    isCleaningUp.current = true;

    try {
      if (trackingState.current.updateInterval) {
        clearInterval(trackingState.current.updateInterval);
        trackingState.current.updateInterval = null;
      }

      cleanupActivityTracking();
      await endSession();
      trackingState.current.initialized = false;
    } catch (_) {
      // TODO: Send error to Sentry or other error monitoring service
    } finally {
      isCleaningUp.current = false;
    }
  }, []);

  /**
   * Handles periodic session updates when user is active.
   * - Updates existing session if within grace period
   * - Starts new session if grace period has expired
   * - Implements rate limiting to prevent excessive API calls
   */
  const handleSessionUpdate = useCallback(async (): Promise<void> => {
    const now = new Date();
    const lastActivity = getLatestActivityTime();

    // Skip update if there's no recent activity or if update would be too frequent
    if (
      !lastActivity ||
      now.getTime() - trackingState.current.lastUpdateTime < UPDATE_INTERVAL_MS
    ) {
      return;
    }

    // Skip update if there's no active session
    const session = getStoredSession();
    if (!session?.id) {
      return;
    }

    try {
      // If last activity was within the grace period, update the session
      if (
        isLatestUpdateWithinGracePeriod(now) &&
        isLatestActivityWithinGracePeriod(now)
      ) {
        await updateSession(session.id);
        trackingState.current.lastUpdateTime = now.getTime();
      } else {
        const token = await getToken();
        await startSession(token);
        trackingState.current.lastUpdateTime = now.getTime();
      }
    } catch (_) {
      // TODO: Send error to Sentry or other error monitoring service
    }
  }, [getToken]);

  /**
   * Initializes activity tracking and session management.
   * - Resumes existing session if within grace period
   * - Creates new session if needed
   * - Sets up activity tracking and periodic updates
   */
  const initializeTracking = useCallback(async (): Promise<void> => {
    if (
      !isUserAnalyticsEnabled ||
      trackingState.current.initialized ||
      isInitializing.current
    ) {
      return;
    }

    isInitializing.current = true;

    try {
      const lastActivity = getLatestActivityTime();
      const session = getStoredSession();

      // Determine whether to resume existing session or start new one
      if (
        session?.id &&
        lastActivity &&
        isLatestUpdateWithinGracePeriod(new Date())
      ) {
        await updateSession(session.id);
      } else {
        const token = await getToken();
        await startSession(token);
      }

      // Initialize activity tracking and periodic updates
      initializeActivityTracking();
      trackingState.current.initialized = true;
      trackingState.current.updateInterval = setInterval(
        handleSessionUpdate,
        UPDATE_INTERVAL_MS,
      );
    } catch (_) {
      // TODO: Send error to Sentry or other error monitoring service
    } finally {
      isInitializing.current = false;
    }
  }, [isUserAnalyticsEnabled, getToken, handleSessionUpdate]);

  /**
   * Effect to handle page visibility changes and session management.
   * - Starts tracking when page becomes visible and session management is enabled
   * - Stops tracking when page becomes hidden or session management is disabled
   * - Cleans up on unmount
   */
  useEffect(() => {
    const handleVisibilityChange = (): void => {
      const isVisible = document.visibilityState === 'visible';

      if (!isVisible || !isUserAnalyticsEnabled) {
        void stopTracking();
        return;
      }

      void initializeTracking();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial setup
    handleVisibilityChange();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      void stopTracking();
    };
  }, [isUserAnalyticsEnabled, stopTracking, initializeTracking]);
}
